import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Landmark, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/FormBits";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1773730145536-a0d2764aa685?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const PLACES = ["Cinema Teatro Narzio", "Sala Conferenze Biblioteca", "Sala Polifunzionale Vignola", "Rocca Abbaziale"];

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try { await login(email, password); } catch (err) { setError(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#F8F6F2]">
      <aside className="relative hidden lg:block overflow-hidden grain">
        <img src={HERO} alt="Borgo storico" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#1E293B]/70" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-md border border-[#C9A45C]/60 bg-[#8B1E2B]"><Landmark className="h-6 w-6 text-[#F3E3BF]" /></span>
            <span className="font-serif text-2xl font-semibold">Comune di Subiaco</span>
          </div>
          <div className="max-w-md">
            <p className="eyebrow !text-[#E8D3A3] mb-4">Area riservata al personale</p>
            <h1 className="font-serif text-5xl font-semibold leading-[1.05]">Gli spazi della città, in un unico registro condiviso.</h1>
            <ul className="mt-10 space-y-3 border-l border-[#C9A45C]/50 pl-5">
              {PLACES.map((p, i) => (
                <li key={p} className="fade-up text-slate-200" style={{ animationDelay: `${150 + i * 90}ms` }}>
                  <span className="font-mono text-xs text-[#E8D3A3] mr-3">0{i + 1}</span>{p}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-slate-400">Ufficio Patrimonio · Cultura · Eventi</p>
        </div>
      </aside>

      <main className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-md bg-[#8B1E2B]"><Landmark className="h-5 w-5 text-[#F3E3BF]" /></span>
            <span className="font-serif text-2xl font-semibold">Comune di Subiaco</span>
          </div>
          <p className="eyebrow mb-3 flex items-center gap-2"><Lock className="h-3 w-3" />Accesso dipendenti</p>
          <h2 className="font-serif text-4xl font-semibold text-slate-900">Accedi al gestionale</h2>
          <p className="mt-2 text-sm text-slate-600">Usa le credenziali fornite dall'amministratore.</p>

          <div className="mt-10 space-y-5">
            <Field label="Email istituzionale">
              <Input data-testid="login-email-input" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white" />
            </Field>
            <Field label="Password">
              <Input data-testid="login-password-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" />
            </Field>
            {error && <p data-testid="login-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="h-11 w-full bg-[#8B1E2B] hover:bg-[#701621] text-base">
              {loading ? "Accesso in corso…" : "Entra"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
